import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { FormInstance, Placeholder } from 'types/step';
import { TopBar } from 'topBar/components/TopBar';
import { useScrolledPast } from 'topBar/hooks/useScrolledPast';

import { usePosthogCapture } from 'form/posthogCapture/_index';
import { General } from 'icons/untitledui';
import { MULTIDATE_SEPARATOR } from 'form/components/FormFieldRenderer/constants';
import { FormOrigin } from 'form/types';

import * as PageLayout from 'layout/styles/PageLayout.style';
import * as Text from 'theme/components/Text.style';
import * as Buttons from 'theme/components/Buttons.style';
import * as Styled from 'form/styles/FormCompletedView.style';

interface FormCompletedViewProps {
  form: FormInstance;
  origin: FormOrigin;
  onEdit: () => void;
  isLoading?: boolean;
  onBack?: () => void;
  embedded?: boolean;
}

function FieldValue({
  placeholder,
  filledData,
}: {
  placeholder: Placeholder;
  filledData: Record<string, any>;
}): JSX.Element {
  const { t } = useTranslation('pageForm');
  const raw = String(filledData[placeholder.placeholderKey] ?? '');
  const emptyValue = <Text.P2>{t('completed.emptyValue')}</Text.P2>;

  switch (placeholder.type) {
    case 'boolean':
      if (!raw) return emptyValue;
      return (
        <Text.P2>
          {raw === 'true' ? t('completed.yes') : t('completed.no')}
        </Text.P2>
      );
    case 'multidate': {
      const dates = raw
        .split(MULTIDATE_SEPARATOR)
        .map((d) => d.trim())
        .filter(Boolean);
      if (!dates.length) return emptyValue;
      return <Text.P2>{dates.join(', ')}</Text.P2>;
    }
    case 'dateTextList': {
      try {
        const items = raw && raw !== '[]' ? JSON.parse(raw) : [];
        const populated = Array.isArray(items)
          ? items.filter((item) => item?.date || item?.text)
          : [];
        if (!populated.length) return emptyValue;
        return (
          <>
            {populated.map((item, index) => (
              <Text.P2 key={index}>
                {[item.date, item.text].filter(Boolean).join(' — ')}
              </Text.P2>
            ))}
          </>
        );
      } catch {
        return emptyValue;
      }
    }
    default:
      return <Text.P2>{raw || t('completed.emptyValue')}</Text.P2>;
  }
}

export default function FormCompletedView({
  form,
  origin,
  onEdit,
  isLoading = false,
  onBack,
  embedded = false,
}: FormCompletedViewProps): JSX.Element {
  const history = useHistory();
  const { t } = useTranslation('pageForm');
  const titleRef = useRef<HTMLDivElement>(null);
  const isScrolledPast = useScrolledPast(titleRef);
  const posthogCapture = usePosthogCapture();

  const templateName =
    form.form_template.document_translation.form_template_name ||
    form.form_template.document_translation.title;

  useEffect(() => {
    posthogCapture.completedViewOpened({
      templateId: form.form_template.id,
      templateName,
      origin,
    });
  }, [form.form_template.id, templateName, origin, posthogCapture]);

  const handleEditClick = () => {
    posthogCapture.completedEditSelected({
      templateId: form.form_template.id,
      templateName,
    });
    onEdit();
  };

  const ContainerEl = embedded ? Styled.EmbeddedContainer : Styled.Container;

  return (
    <PageLayout.ContentContainer>
      <TopBar
        onBackClick={onBack ?? history.goBack}
        showOnDesktop
        title={templateName}
        showTitle={isScrolledPast}
        menuItems={[
          {
            label: t('form.edit'),
            onClick: handleEditClick,
            icon: <General.Edit05 size={20} />,
            disabled: isLoading,
          },
          {
            label: t('form.download'),
            onClick: () => {
              window.open(form.file, '_blank', 'noopener,noreferrer');
              posthogCapture.downloadClicked({
                templateId: form.form_template.id,
              });
            },
            icon: <General.Download01 size={20} />,
            disabled: isLoading,
          },
        ]}
      />
      <ContainerEl>
        <PageLayout.PageTitleContainer
          ref={titleRef}
          style={{ flexDirection: 'column', alignItems: 'flex-start' }}
        >
          <Text.H3>{templateName}</Text.H3>
        </PageLayout.PageTitleContainer>
        <Styled.Card>
          <Styled.RowsContainer>
            {form.form_template.placeholders.map((placeholder) => (
              <Styled.DetailRow key={placeholder.placeholderKey}>
                <Text.P2Semibold>{placeholder.displayName}</Text.P2Semibold>
                <FieldValue
                  placeholder={placeholder}
                  filledData={form.filled_data}
                />
              </Styled.DetailRow>
            ))}
          </Styled.RowsContainer>
          <Styled.Actions>
            {isLoading ? (
              <Buttons.SecondaryLight $size="small" disabled>
                <Styled.SpinningIcon>
                  <General.Loading02 size={20} />
                </Styled.SpinningIcon>
                {t('completed.generating')}
              </Buttons.SecondaryLight>
            ) : (
              <Buttons.SecondaryLight
                as="a"
                $size="small"
                href={form.file}
                onClick={() =>
                  posthogCapture.downloadClicked({
                    templateId: form.form_template.id,
                  })
                }
              >
                {t('form.download')}
              </Buttons.SecondaryLight>
            )}
          </Styled.Actions>
        </Styled.Card>
      </ContainerEl>
    </PageLayout.ContentContainer>
  );
}
